import React from 'react';

const FoldDiagram = ({ foldType, width, height, isDoubleSided }) => {
  // Determine orientation for the diagram
  const isLandscape = width >= height;
  
  // Calculate aspect ratio for the container
  const aspectRatio = isLandscape ? 3 : 0.33;
  
  // Calculate number of panels based on fold type
  let panelCount = 1;
  switch(foldType) {
    case 'half': panelCount = 2; break;
    case 'tri':
    case 'z': panelCount = 3; break;
    case 'gate': panelCount = 3; break;
    case 'cross': panelCount = 4; break;
    case 'accordion': panelCount = 4; break;
    case 'parallel': panelCount = 2; break;
    default: panelCount = 1;
  }
  
  // Generate panel colors (alternate for clarity)
  const getPanelColor = (index, isDoubleSided) => {
    const colors = ['bg-blue-100', 'bg-blue-200', 'bg-blue-300', 'bg-blue-400'];
    
    if (!isDoubleSided) {
      return colors[index % colors.length];
    }
    
    // For double-sided, use different pattern
    if (index % 2 === 0) {
      return colors[Math.floor(index/2) % colors.length];
    } else {
      return 'bg-gray-200';
    }
  };
  
  // Fold-specific layout functions
  const renderHalfFold = () => {
    return (
      <div className={`flex ${isLandscape ? 'flex-row' : 'flex-col'} h-full`}>
        <div className={`flex-1 border ${getPanelColor(0, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 1 {isDoubleSided && "(Front)"}</div>
        </div>
        <div className={`flex-1 border ${getPanelColor(1, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 2 {isDoubleSided && "(Back)"}</div>
        </div>
      </div>
    );
  };
  
  const renderTriFold = () => {
    return (
      <div className={`flex ${isLandscape ? 'flex-row' : 'flex-col'} h-full`}>
        <div className={`flex-1 border ${getPanelColor(0, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 1</div>
        </div>
        <div className={`flex-1 border ${getPanelColor(1, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 2</div>
        </div>
        <div className={`flex-1 border ${getPanelColor(2, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 3</div>
        </div>
      </div>
    );
  };
  
  const renderZFold = () => {
    // Z-fold has the same panel layout as tri-fold but different folding direction
    return renderTriFold();
  };
  
  const renderGateFold = () => {
    const sidePanelSize = "33%";
    const centerPanelSize = "34%";
    
    return (
      <div className={`flex ${isLandscape ? 'flex-row' : 'flex-col'} h-full`}>
        <div 
          className={`border ${getPanelColor(0, isDoubleSided)} flex items-center justify-center`}
          style={{ [isLandscape ? 'width' : 'height']: sidePanelSize }}
        >
          <div className="text-xs font-medium">Panel 1</div>
        </div>
        <div 
          className={`border ${getPanelColor(1, isDoubleSided)} flex items-center justify-center`}
          style={{ [isLandscape ? 'width' : 'height']: centerPanelSize }}
        >
          <div className="text-xs font-medium">Panel 2</div>
        </div>
        <div 
          className={`border ${getPanelColor(2, isDoubleSided)} flex items-center justify-center`}
          style={{ [isLandscape ? 'width' : 'height']: sidePanelSize }}
        >
          <div className="text-xs font-medium">Panel 3</div>
        </div>
      </div>
    );
  };
  
  const renderCrossFold = () => {
    return (
      <div className="grid grid-cols-2 grid-rows-2 h-full">
        <div className={`border ${getPanelColor(0, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 1</div>
        </div>
        <div className={`border ${getPanelColor(1, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 2</div>
        </div>
        <div className={`border ${getPanelColor(2, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 3</div>
        </div>
        <div className={`border ${getPanelColor(3, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 4</div>
        </div>
      </div>
    );
  };
  
  const renderAccordionFold = () => {
    return (
      <div className={`flex ${isLandscape ? 'flex-row' : 'flex-col'} h-full`}>
        <div className={`flex-1 border ${getPanelColor(0, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 1</div>
        </div>
        <div className={`flex-1 border ${getPanelColor(1, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 2</div>
        </div>
        <div className={`flex-1 border ${getPanelColor(2, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 3</div>
        </div>
        <div className={`flex-1 border ${getPanelColor(3, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 4</div>
        </div>
      </div>
    );
  };
  
  const renderParallelFold = () => {
    return (
      <div className={`flex ${isLandscape ? 'flex-col' : 'flex-row'} h-full`}>
        <div className={`flex-1 border ${getPanelColor(0, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 1</div>
        </div>
        <div className={`flex-1 border ${getPanelColor(1, isDoubleSided)} flex items-center justify-center`}>
          <div className="text-xs font-medium">Panel 2</div>
        </div>
      </div>
    );
  };
  
  // For double-sided, show information about the back side
  const renderDoubleSidedInfo = () => {
    if (!isDoubleSided) return null;
    
    return (
      <div className="mt-2 text-xs text-gray-600">
        <div className="flex items-center mb-1">
          <div className="w-3 h-3 bg-blue-200 mr-1"></div>
          <span>Front panels</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-200 mr-1"></div>
          <span>Back panels (when printed double-sided)</span>
        </div>
      </div>
    );
  };
  
  // Render the appropriate diagram based on fold type
  const renderFoldDiagram = () => {
    switch(foldType) {
      case 'half': return renderHalfFold();
      case 'tri': return renderTriFold();
      case 'z': return renderZFold();
      case 'gate': return renderGateFold();
      case 'cross': return renderCrossFold();
      case 'accordion': return renderAccordionFold();
      case 'parallel': return renderParallelFold();
      default: return <div className="text-center text-gray-500">Select a fold type</div>;
    }
  };
  
  // Get fold description
  const getFoldDescription = () => {
    switch(foldType) {
      case 'half': return 'Half fold creates 2 panels by folding once.';
      case 'tri': return 'Tri-fold creates 3 panels with 2 parallel folds.';
      case 'z': return 'Z-fold creates 3 panels in a zig-zag pattern.';
      case 'gate': return 'Gate fold has 2 panels that fold inward to meet at the center.';
      case 'cross': return 'Cross fold creates 4 panels with perpendicular folds.';
      case 'accordion': return 'Accordion fold creates 4 panels with a zig-zag pattern.';
      case 'parallel': return 'Parallel fold creates 2 panels by folding perpendicular to the orientation.';
      default: return '';
    }
  };
  
  return (
    <div>
      <div 
        className="mb-2 border rounded overflow-hidden"
        style={{ 
          height: '120px', 
          aspectRatio: aspectRatio 
        }}
      >
        {renderFoldDiagram()}
      </div>
      <p className="text-xs text-gray-600 mb-2">{getFoldDescription()}</p>
      {renderDoubleSidedInfo()}
      <div className="mt-2 text-xs text-gray-500">
        <p>You selected a <strong>{Math.round(width)}mm × {Math.round(height)}mm</strong> sheet.</p>
        <p>When folded, this will create {panelCount * (isDoubleSided ? 2 : 1)} viewable {panelCount * (isDoubleSided ? 2 : 1) === 1 ? 'page' : 'pages'}.</p>
      </div>
    </div>
  );
};

export default FoldDiagram;